
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import ScrollButtons from '../../components/feature/ScrollButtons';

export default function About() {
  const navigate = useNavigate();

  const timeline = [
    {
      year: "1945",
      title: "Foundation",
      description: "Bible Believers Association was founded by Bro. A.N. Jonah with a deep burden to serve the poor, sick, and marginalized with Christ's love.",
      icon: "ri-seedling-line"
    },
    {
      year: "1970",
      title: "Expansion Under Bishop Daniel Evans",
      description: "Bishop Daniel Evans took leadership and expanded the work into children's homes, widows' care, leprosy ministry, sewing centers, and scholarships.",
      icon: "ri-building-line"
    },
    {
      year: "2020",
      title: "Third Generation Leadership",
      description: "Bishop Ameeth Evans continues the mission with renewed focus on education, healthcare, women's empowerment, and clean water initiatives.",
      icon: "ri-heart-line"
    }
  ];

  const leadership = [
    {
      name: "Bro. A.N. Jonah",
      role: "Founder (1945)",
      description: "Founded Bible Believers Association with a vision to serve the marginalized with Christ's love.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/9be4ee836c0ef4345f5d6532e2d25468.png"
    },
    {
      name: "Bishop Daniel Evans",
      role: "Second Generation Leader (54 years)",
      description: "Expanded the ministry into comprehensive community programs including children's homes, widows' care, and educational initiatives.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/72a102770def48206b442024df72d758.jfif"
    },
    {
      name: "Bishop Ameeth Evans",
      role: "Current Leader - Third Generation",
      description: "Leading the ministry into new areas of outreach with focus on education, healthcare, women's empowerment, and clean water initiatives.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/9ac026394acc713c69095457677eae8d.jfif"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Mobile Optimized */}
      <section 
        className="relative h-64 md:h-96 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/a13d8f9034d90fc2b241932c9dae746a.jpg')`
        }}
      >
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
                About Us
              </h1>
              <p className="text-base md:text-xl text-gray-200 max-w-3xl mx-auto">
                79 years of faithful service, three generations of leadership, countless lives transformed through God's love
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story - Mobile Optimized */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">Our Story</h2>
              <div className="space-y-4 md:space-y-6 text-sm md:text-lg text-gray-600 leading-relaxed">
                <p>
                  Bible Believers Association was founded 79 years ago by Bro. A.N. Jonah, who carried a deep burden to serve the poor, the sick, and the marginalized with the love of Christ. What began as a small effort has grown into a movement of compassion, reaching countless lives across India.
                </p>
                <p>
                  For the past 54 years, Bishop Daniel Evans has faithfully carried forward this vision—expanding the work into children's homes, widows' care, leprosy ministry, sewing centers, scholarships, and community projects. Under his leadership, the ministry was firmly rooted in prayer, integrity, and a heart for those in need.
                </p>
                <p>
                  Today, the third generation—Bishop Ameeth Evans—continues this God-given mission. With the same passion and a renewed focus on the younger generation, he is leading the ministry into new areas of outreach, including education, healthcare, women's empowerment, and clean water initiatives.
                </p>
                <p>
                  From small beginnings, God has blessed this work to become a beacon of hope—bringing dignity, faith, and opportunity to communities in need. Every project, whether a scholarship for a child, a well for a village, or a safe home for widows, is a testimony to God's faithfulness and the power of His love in action.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/cf0841719708c2a04fc0f65f15ac512f.jfif"
                alt="Our Ministry Work"
                className="w-full h-64 md:h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline - Mobile Optimized */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Our Journey</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Three generations of faithful leadership, expanding God's work across India
            </p>
          </div>
          
          {/* Mobile Timeline - Vertical Stack */}
          <div className="md:hidden space-y-6">
            {timeline.map((item, index) => (
              <div key={index} className="bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-lg text-white`}></i>
                  </div>
                  <div className="text-xl font-bold text-blue-600">{item.year}</div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Desktop Timeline - Original Design */}
          <div className="hidden md:block relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-200"></div>
            
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                      <div className="text-2xl font-bold text-blue-600 mb-2">{item.year}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                  
                  <div className="relative z-10 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership - Mobile Optimized */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Our Leadership</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Three generations of faithful leaders who have dedicated their lives to serving others
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {leadership.map((leader, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-4 md:mb-6">
                  <img 
                    src={leader.image}
                    alt={leader.name}
                    className="w-32 h-40 md:w-48 md:h-64 object-cover object-top rounded-xl mx-auto shadow-lg"
                  />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{leader.name}</h3>
                <div className="text-blue-600 font-semibold mb-2 md:mb-3 text-sm md:text-base">{leader.role}</div>
                <p className="text-gray-600 text-sm md:text-base">{leader.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Beliefs, Mission & Vision - Mobile Optimized */}
      <section className="py-12 md:py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-12">
            
            {/* Our Beliefs */}
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg">
              <div className="text-center mb-6 md:mb-8">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-blue-600 rounded-full flex items-center justify-center">
                  <i className="ri-book-open-line text-lg md:text-2xl text-white"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">Our Beliefs</h3>
              </div>
              <div className="space-y-3 md:space-y-4 text-gray-700">
                <p className="font-medium mb-3 md:mb-4 text-sm md:text-base">We hold to the Oneness Apostolic faith as revealed in the Scriptures:</p>
                <ul className="space-y-2 md:space-y-3 text-xs md:text-sm leading-relaxed">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1 mr-2 md:mr-3 flex-shrink-0"></i>
                    <span>There is One God, who has revealed Himself as Father in creation, Son in redemption, and Holy Spirit in regeneration.</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1 mr-2 md:mr-3 flex-shrink-0"></i>
                    <span>The Bible is the inspired, infallible Word of God and the final authority for faith and life.</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1 mr-2 md:mr-3 flex-shrink-0"></i>
                    <span>Salvation comes through faith in Jesus Christ, repentance from sin, water baptism in the Name of Jesus, and the infilling of the Holy Spirit with the evidence of speaking in tongues.</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1 mr-2 md:mr-3 flex-shrink-0"></i>
                    <span>We believe in living a life of holiness, both inwardly and outwardly, as we pursue a Christlike witness in this world.</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mt-1 mr-2 md:mr-3 flex-shrink-0"></i>
                    <span>We await the soon return of our Lord Jesus Christ and the gathering of His church.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Our Mission */}
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg">
              <div className="text-center mb-6 md:mb-8">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-blue-600 rounded-full flex items-center justify-center">
                  <i className="ri-compass-3-line text-lg md:text-2xl text-white"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                To preach the Gospel of Jesus Christ and demonstrate His love through practical works of compassion—caring for children, widows, the sick, and the marginalized, while empowering communities through education, clean water, and sustainable development.
              </p>
            </div>

            {/* Our Vision */}
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg">
              <div className="text-center mb-6 md:mb-8">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-blue-600 rounded-full flex items-center justify-center">
                  <i className="ri-eye-line text-lg md:text-2xl text-white"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                To see lives transformed by the power of the Holy Spirit, communities uplifted by the love of Christ, and future generations raised up as leaders in faith, dignity, and service—fulfilling the Great Commission in our nation and beyond.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Call to Action - Mobile Optimized */}
      <section className="py-12 md:py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 md:mb-6">
            Join Our Mission
          </h2>
          <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8 leading-relaxed">
            Be part of this 79-year legacy of faith, hope, and transformation. Together, we can continue bringing God's love to those who need it most.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Button 
              variant="primary" 
              size="md"
              onClick={() => navigate('/donate')}
              className="!bg-white !text-blue-600 hover:!bg-gray-100 hover:!text-blue-600 shadow-lg"
            >
              <div className="flex items-center space-x-2">
                <i className="ri-heart-fill"></i>
                <span>Support Our Work</span>
              </div>
            </Button>
            <Button 
              variant="outline" 
              size="md"
              onClick={() => navigate('/projects')}
              className="border-white text-white hover:bg-white hover:text-blue-600"
            >
              <div className="flex items-center space-x-2">
                <i className="ri-eye-line"></i>
                <span>View Our Projects</span>
              </div>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollButtons />
    </div>
  );
}
